// Autodiagnóstico de conexión para la pantalla de login.
//
// Una usuaria en un hospital pasó horas cambiando su contraseña cuando lo que
// fallaba era el firewall de la institución bloqueando backend.smardx.com.
// Confirmado por DNS: app.smardx.com resuelve a Cloudflare y backend.smardx.com
// a AWS — son redes distintas, así que una institución puede dejar pasar la app
// y cortar el backend. En CloudWatch los requests de la usuaria nunca llegaron.
//
// Este helper responde una sola pregunta: el navegador de este médico, en esta
// red, ¿alcanza el backend? Devuelve un veredicto legible para alguien no
// técnico + un detalle duro para mandarle una captura a soporte.
//
// Sondea /health/ (backend/app/app/urls.py), que responde 200 sin auth ni CSRF.

import { esMantenimientoRespuesta } from "./get-client";

const PROBE_TIMEOUT_MS = 8000;

const backendBase = () =>
  (process.env.REACT_APP_BACKEND_URI || "").replace(/\/+$/, "");

const getHealthUrl = () => `${backendBase()}/health/`;

const fetchWithTimeout = async (url, init, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

// Bloque técnico que el médico le manda a soporte por captura o copiando.
const technicalDetail = (lines) =>
  [
    ...lines,
    `Página: ${window.location.origin}`,
    `Navegador reporta conexión: ${navigator.onLine ? "sí" : "no"}`,
    `Hora: ${new Date().toLocaleString("es-CL")}`,
  ].join("\n");

const sondearBackendDirecto = async () => {
  const url = getHealthUrl();
  const startedAt = Date.now();

  if (!backendBase()) {
    return {
      ok: false,
      kind: "config",
      title: "Conexión con el servidor: NO CONFIGURADA",
      hint: "Es un problema de la aplicación, no de tu red ni de tu clave. Avísale a soporte.",
      detail: technicalDetail([
        "REACT_APP_BACKEND_URI está vacío en el build del frontend.",
      ]),
    };
  }

  try {
    const response = await fetchWithTimeout(
      url,
      { method: "GET", cache: "no-store", credentials: "omit" },
      PROBE_TIMEOUT_MS,
    );
    const elapsed = Date.now() - startedAt;

    if (response.ok) {
      return {
        ok: true,
        kind: "ok",
        title: "Conexión con el servidor: OK",
        // Deliberadamente NO menciona la contraseña. El consejo sobre la clave
        // lo agrega la UI (hintFor) y solo cuando el login falló por
        // credenciales: la sonda corre después del fallo, con el backend ya
        // caliente, así que dice OK en casos donde la clave está perfecta.
        hint: "El servidor responde bien desde esta red, así que la conexión no es el problema.",
        detail: technicalDetail([
          `URL probada: ${url}`,
          `Respuesta HTTP: ${response.status} en ${elapsed} ms`,
        ]),
      };
    }

    // Un 403/407/451 que no viene en JSON no lo contestó nuestro servidor: es el
    // filtro de la red devolviendo su página de bloqueo. Sin esto el panel le
    // decía al médico "el servidor contestó algo inesperado, muéstrale esto a
    // soporte" cuando lo que tenía que hacer era hablar con informática de su
    // institución. Mismo criterio que `esRespuestaAjena` en el login.
    const tipoRespuesta = String(response.headers.get("content-type") || "");
    const esBloqueo =
      [403, 407, 451].includes(response.status) && !tipoRespuesta.includes("json");
    if (esBloqueo) {
      return {
        ok: false,
        kind: "blocked",
        title: "Conexión con el servidor: BLOQUEADA POR TU RED",
        hint: "Algo en la red de tu institución está bloqueando el servidor de SmartDx y respondió por él. No es tu contraseña. Muéstrale este detalle al área de informática.",
        detail: technicalDetail([
          `URL probada: ${url}`,
          `Respuesta HTTP: ${response.status} en ${elapsed} ms`,
          `Tipo de contenido: ${tipoRespuesta || "(sin declarar)"} — nuestro servidor siempre responde JSON`,
        ]),
      };
    }

    return {
      ok: false,
      kind: "http",
      title: `Conexión con el servidor: RESPONDE CON ERROR (${response.status})`,
      hint:
        response.status >= 500
          ? "El servidor está con problemas. No es tu clave — muéstrale esto a soporte."
          : "El servidor contestó algo inesperado. Muéstrale esto a soporte.",
      detail: technicalDetail([
        `URL probada: ${url}`,
        `Respuesta HTTP: ${response.status} ${response.statusText} en ${elapsed} ms`,
      ]),
    };
  } catch (error) {
    const elapsed = Date.now() - startedAt;
    const aborted = error?.name === "AbortError";

    if (aborted) {
      return {
        ok: false,
        kind: "timeout",
        title: "Conexión con el servidor: SIN RESPUESTA",
        hint: "El servidor no contestó a tiempo. Suele pasar cuando la red de la institución filtra el tráfico. Prueba con los datos móviles de tu teléfono: si ahí entras, el problema es la red del hospital.",
        detail: technicalDetail([
          `URL probada: ${url}`,
          `Resultado: sin respuesta HTTP (timeout a los ${elapsed} ms)`,
          `Tipo de error: AbortError (timeout de ${PROBE_TIMEOUT_MS} ms)`,
        ]),
      };
    }

    // Un fetch bloqueado por firewall y uno rechazado por CORS dan el MISMO
    // TypeError "Failed to fetch". Una segunda sonda en modo no-cors aporta
    // algo de señal: si resuelve, ALGO contestó en esa dirección.
    //
    // OJO: una respuesta opaca no expone el status, así que resuelve igual con
    // un 401, un 403 o con la página de bloqueo de un proxy corporativo. NO
    // permite concluir que el que contestó sea nuestro servidor — un firewall
    // de hospital que intercepta (en vez de descartar) el paquete cae acá. Por
    // eso el veredicto se redacta como "no confirmado" y nunca como "el
    // problema es nuestro".
    let somethingAnswered = false;
    try {
      await fetchWithTimeout(
        url,
        {
          method: "GET",
          mode: "no-cors",
          cache: "no-store",
          credentials: "omit",
        },
        PROBE_TIMEOUT_MS,
      );
      somethingAnswered = true;
    } catch {
      somethingAnswered = false;
    }

    const base = [
      `URL probada: ${url}`,
      "Resultado: no hubo respuesta HTTP (el request nunca volvió)",
      `Tipo de error: ${error?.name || "Error"} — ${error?.message || "sin mensaje"}`,
      `Tiempo hasta el fallo: ${elapsed} ms`,
      somethingAnswered
        ? "Sonda sin CORS: algo respondió, pero la respuesta es opaca (no expone el código HTTP), así que NO permite distinguir el servidor real de un proxy o portal cautivo de la red."
        : "Sonda sin CORS: tampoco respondió nada.",
    ];

    if (somethingAnswered) {
      return {
        ok: false,
        kind: "unconfirmed",
        title: "Conexión con el servidor: RESPUESTA NO CONFIRMADA",
        hint: "Algo respondió en esa dirección, pero no pudimos confirmar que sea el servidor de SmartDx: puede ser un intermediario de tu red (un proxy o una página de bloqueo de tu institución). No es tu contraseña. Muéstrale este detalle a soporte y al área de informática de tu institución.",
        detail: technicalDetail(base),
      };
    }

    return {
      ok: false,
      kind: "blocked",
      title: "Conexión con el servidor: BLOQUEADA",
      hint: "Tu red no deja llegar al servidor de SmartDx. No es tu contraseña. Muéstrale este detalle a soporte o al área de informática de tu institución, y prueba entrar con los datos móviles de tu teléfono para confirmarlo.",
      detail: technicalDetail(base),
    };
  }
};


// ── La vía de rescate ────────────────────────────────────────────────────────
// El sondeo de arriba pregunta si esta red llega al backend DIRECTO. Esa no es
// la pregunta que le importa al médico: le importa si puede trabajar. Y puede,
// si el rescate same-origin (`app.smardx.com/api/*` → Pages Function) pasa.
//
// Sin esto, el panel le decía "Conexión BLOQUEADA" a alguien que estaba
// entrando y trabajando por el proxy, y lo mandaba a llamar a soporte igual.
//
// Se sondea `/api/user/csrf/` y no `/health/` a propósito: la Pages Function
// solo cubre `/api/*`, y `/health/` same-origin devuelve el index.html de la
// SPA (un 200 en text/html), o sea un falso OK.
const RESCATE_URL = "/api/user/csrf/";

// Más corto que el sondeo directo a propósito: los dos son secuenciales, y con
// 8 s cada uno el peor caso (los dos caminos cortados en silencio) dejaba al
// médico 16 s mirando "Revisando la conexión...". Este es same-origin contra un
// endpoint que ya respondió para servir la app, así que 4 s sobran.
const RESCATE_TIMEOUT_MS = 4000;

const sondearRescate = async () => {
  const startedAt = Date.now();
  try {
    const response = await fetchWithTimeout(
      RESCATE_URL,
      { method: "GET", cache: "no-store", credentials: "omit" },
      RESCATE_TIMEOUT_MS,
    );
    const elapsed = Date.now() - startedAt;
    const tipo = String(response.headers.get("content-type") || "");
    // El index.html de la SPA también contesta 200: sin este chequeo, un
    // rescate roto se leería como rescate sano.
    const esNuestro = response.ok && tipo.includes("json");
    return { ok: esNuestro, status: response.status, tipo, elapsed };
  } catch (error) {
    return {
      ok: false,
      error: `${error?.name || "Error"} — ${error?.message || "sin mensaje"}`,
      elapsed: Date.now() - startedAt,
    };
  }
};

// Lo que el médico le reenvía a informática. Son los dominios del CSP, que es
// la lista real de lo que la app necesita abierto.
const PARA_INFORMATICA = [
  "Dominios que SmartDx necesita abiertos (TCP 443):",
  "  app.smardx.com          la aplicación",
  "  backend.smardx.com      la API",
  "  accounts.google.com     para entrar con Google",
  "  api.deepgram.com        dictado por voz (y wss:// para el mismo host)",
].join("\n");

const checkBackendReachability = async () => {
  const directo = await sondearBackendDirecto();
  if (directo.ok) return directo;

  // `config` = el build salió sin `REACT_APP_BACKEND_URI`. Es un problema
  // NUESTRO y el sondeo directo ya lo dice con todas las letras. Sondear el
  // rescate acá era una regresión: el same-origin contesta 200 JSON igual, el
  // veredicto pasaba a "tu red bloquea el camino directo", y un médico con un
  // build roto terminaba abriendo un ticket en informática de su clínica por
  // un bug nuestro.
  if (directo.kind === "config") return directo;

  // El directo falló por la red. La pregunta que queda es si igual puede trabajar.
  const rescate = await sondearRescate();
  if (rescate.ok) {
    return {
      ok: true,
      kind: "proxy",
      title: "Conexión con el servidor: POR LA VÍA ALTERNATIVA",
      hint: "Tu red bloquea el camino directo al servidor, pero la app está entrando por una ruta alternativa y puedes trabajar normal. Si igual quieres que quede bien resuelto, muéstrale este detalle a informática de tu institución.",
      detail: [directo.detail, "", "Vía alternativa: OK", PARA_INFORMATICA].join("\n"),
    };
  }

  // La vía alternativa respondió 502/503 legible (nginx/ALB/CloudFront, HTML): el
  // directo no contesta porque SmartDx se está actualizando, no porque la red lo
  // bloquee (el 502 sin CORS se ve igual que un firewall). Veredicto de servidor
  // con problemas, el 5xx de siempre y sin texto nuevo; no "habilita los dominios".
  // Cubre R17 — .claude/specs/1757-login-mantenimiento/
  if (esMantenimientoRespuesta(rescate.status, rescate.tipo)) {
    return {
      ok: false,
      kind: "http",
      title: `Conexión con el servidor: RESPONDE CON ERROR (${rescate.status})`,
      hint: "El servidor está con problemas. No es tu clave — muéstrale esto a soporte.",
      detail: [
        directo.detail,
        "",
        `Vía alternativa (${RESCATE_URL}): HTTP ${rescate.status} ${rescate.tipo}`,
      ].join("\n"),
    };
  }

  // Ni el directo ni el rescate: acá sí está afuera, y hay que decirlo con la
  // lista de dominios lista para copiar y pegar en un ticket.
  return {
    ...directo,
    hint: `${directo.hint} La vía alternativa tampoco pasa, así que hay que habilitar los dominios en tu red.`,
    detail: [
      directo.detail,
      "",
      `Vía alternativa (${RESCATE_URL}): ${rescate.error || `HTTP ${rescate.status} ${rescate.tipo}`}`,
      PARA_INFORMATICA,
    ].join("\n"),
  };
};

export { checkBackendReachability, getHealthUrl, sondearBackendDirecto };

